import React, { useState, useEffect } from "react";
import {
  X,
  User,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Users,
  Coins,
  Settings2,
  ClipboardCheck,
  CloudCog,
  IdCard,
  Building2,
  Stamp,
  FileSignature,
  Barcode,
  Binary,
  Loader2,
  Edit,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { ISalesRepInfo } from "../../models/epcCustomer.model";
import { usePreventWheelChange } from "../../utils/usePreventWheelChange";
import { getSpecificRoleUsers } from "../../store/slices/epcSlice";
import Button, { ButtonType } from "../Assets/Button";

interface EditSalesRepModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (salesRep: ISalesRepInfo) => void;
  loading?: boolean;
}

const EditSalesRepModal: React.FC<EditSalesRepModalProps> = ({
  isOpen,
  onClose,
  onSave,
  loading = false,
}) => {
  const handleWheelPrevent = usePreventWheelChange();
  const dispatch: RootDispatch = useDispatch();

  const specificRoleUsers = useSelector(
    (state: RootState) => state.epc.specificRoleUsers,
  );
  const usersLoading = useSelector(
    (state: RootState) =>
      state.epc.loadingState.fetchingSpecificRoleUsersLoading,
  );

  const salesRep = useSelector((state: RootState) => state.epc.salesRep);
  const [formData, setFormData] = useState<ISalesRepInfo>({
    name: "",
    mobileNumber: "",
    address: "",
    gst: "",
    panNumber: "",
    bankAccountNumber: "",
    bankAccountName: "",
    ifsc: "",
    email: "",
    companyName: "",
    category: "Channel Partner",
    commissionValue: undefined,
    commissionType: undefined,
    fileCharge: undefined,
    portalCharge: undefined,
    cseId: undefined,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (salesRep && isOpen) {
      setFormData({
        _id: salesRep._id,
        name: salesRep.name || "",
        mobileNumber: salesRep.mobileNumber || "",
        address: salesRep.address || "",
        gst: salesRep.gst || "",
        panNumber: salesRep.panNumber || "",
        bankAccountNumber: salesRep.bankAccountNumber || "",
        bankAccountName: salesRep.bankAccountName || "",
        ifsc: salesRep.ifsc || "",
        email: salesRep.email || "",
        companyName: salesRep.companyName || "",
        category: salesRep.category || "Channel Partner",
        commissionValue: salesRep.commissionValue,
        commissionType: salesRep.commissionType,
        fileCharge: salesRep.fileCharge,
        portalCharge: salesRep.portalCharge,
        cseId: salesRep.cseId,
      });
      setErrors({});
    }
  }, [salesRep, isOpen]);

  useEffect(() => {
    if (isOpen) {
      dispatch(getSpecificRoleUsers());
    }
  }, [isOpen, dispatch]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    if (name === "cseId") {
      const selectedUser = specificRoleUsers.find((user) => user._id === value);
      setFormData((prev) => ({
        ...prev,
        cseId: selectedUser || undefined,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber.replace(/\s+/g, ""))) {
      newErrors.mobileNumber = "Please enter a valid 10-digit mobile number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.category === "Commission Dealer") {
      if (!formData.commissionValue) {
        newErrors.commissionValue = "Commission value is required";
      }

      if (!formData.commissionType) {
        newErrors.commissionType = "Commission type is required";
      }
    }

    if (formData.category === "Channel Partner") {
      if (formData.fileCharge === undefined || formData.fileCharge < 0) {
        newErrors.fileCharge = "File charge is required";
      }

      if (formData.portalCharge === undefined || formData.portalCharge < 0) {
        newErrors.portalCharge = "Portal charge is required";
      }
    }

    if (
      formData.panNumber &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)
    ) {
      newErrors.panNumber =
        "Please enter a valid PAN number (e.g., ABCDE1234F)";
    }

    if (formData.ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc)) {
      newErrors.ifsc = "Please enter a valid IFSC code";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      mobileNumber: "",
      address: "",
      gst: "",
      panNumber: "",
      bankAccountNumber: "",
      bankAccountName: "",
      ifsc: "",
      email: "",
      companyName: "",
      category: "Channel Partner",
      cseId: undefined,
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black/20 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-2xl transform overflow-hidden rounded-xl bg-white shadow-xl border border-gray-100">
          <div className="px-8 py-4 border-b border-gray-100 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-1">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center">
                  <Edit className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Edit Sales Representative
                  </h2>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-8 max-h-[60vh] overflow-y-auto">
            <div className="space-y-8">
              {/* Basic Information */}
              <div className="space-y-6 text-xs">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-6 bg-blue-500 rounded-full"></div>
                  Basic Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-600" />
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 ${
                          errors.name
                            ? "border-red-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        placeholder="Enter full name"
                      />
                    </div>
                    {errors.name && (
                      <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-600" />
                      Mobile Number <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono ${
                          errors.mobileNumber
                            ? "border-red-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        placeholder="Enter mobile number"
                        maxLength={10}
                      />
                    </div>
                    {errors.mobileNumber && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.mobileNumber}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-600" />
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 ${
                          errors.email
                            ? "border-red-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                        placeholder="Enter email address"
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Users className="w-4 h-4 text-gray-600" />
                      Assign CSE <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="cseId"
                        value={formData.cseId?._id || ""}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 hover:border-gray-300"
                        disabled={usersLoading}
                      >
                        <option value="">
                          {usersLoading ? "Loading users..." : "Select CSE"}
                        </option>
                        {specificRoleUsers.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.username}
                          </option>
                        ))}
                      </select>
                    </div>
                    {usersLoading && (
                      <p className="mt-1 text-xs text-gray-500">
                        Loading users...
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-gray-600" />
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 hover:border-gray-300"
                  >
                    <option value="Commission Dealer">Commission Dealer</option>
                    <option value="Channel Partner">Channel Partner</option>
                  </select>
                </div>

                {formData.category === "Commission Dealer" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Coins className="w-4 h-4 text-gray-600" />
                        Commission Value <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="commissionValue"
                        value={formData.commissionValue ?? ""}
                        onChange={handleInputChange}
                        onWheel={handleWheelPrevent}
                        className={`w-full px-4 py-3 border rounded-md focus:ring-2 outline-none ${
                          errors.commissionValue
                            ? "border-red-300"
                            : "border-gray-200 focus:ring-blue-500/20"
                        }`}
                        placeholder="Enter commission value"
                      />
                      {errors.commissionValue && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.commissionValue}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Settings2 className="w-4 h-4 text-gray-600" />
                        Commission Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="commissionType"
                        value={formData.commissionType ?? ""}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none ${
                          errors.commissionType
                            ? "border-red-300"
                            : "border-gray-200"
                        }`}
                      >
                        <option value="">Select commission type</option>
                        <option value="per_kw">Per kW</option>
                        <option value="per_order">Per Order</option>
                      </select>
                      {errors.commissionType && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.commissionType}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {formData.category === "Channel Partner" && (
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4 text-gray-600" />
                        File Charge <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="fileCharge"
                        value={formData.fileCharge ?? ""}
                        onChange={handleInputChange}
                        onWheel={handleWheelPrevent}
                        className={`w-full px-4 py-3 border rounded-md focus:ring-2 outline-none ${
                          errors.fileCharge
                            ? "border-red-300"
                            : "border-gray-200 focus:ring-blue-500/20"
                        }`}
                        placeholder="Enter file charge"
                      />
                      {errors.fileCharge && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.fileCharge}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <CloudCog className="w-4 h-4 text-gray-600" />
                        Portal Charge <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        name="portalCharge"
                        value={formData.portalCharge ?? ""}
                        onChange={handleInputChange}
                        onWheel={handleWheelPrevent}
                        className={`w-full px-4 py-3 border rounded-md focus:ring-2 outline-none ${
                          errors.portalCharge
                            ? "border-red-300"
                            : "border-gray-200 focus:ring-blue-500/20"
                        }`}
                        placeholder="Enter portal charge"
                      />
                      {errors.portalCharge && (
                        <p className="mt-1 text-xs text-red-600">
                          {errors.portalCharge}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 resize-none hover:border-gray-300"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>

              {/* Business Information */}
              <div className="space-y-6 text-xs">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-6 bg-green-500 rounded-full"></div>
                  Business Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <IdCard className="w-4 h-4 text-gray-600" />
                      PAN Number
                    </label>
                    <input
                      type="text"
                      name="panNumber"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono ${
                        errors.panNumber
                          ? "border-red-300"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      style={{ textTransform: "uppercase" }}
                    />
                    {errors.panNumber && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.panNumber}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-600" />
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                      placeholder="Enter company name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Stamp className="w-4 h-4 text-gray-600" />
                    GST Number
                  </label>
                  <input
                    type="text"
                    name="gst"
                    value={formData.gst}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono hover:border-gray-300"
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
              </div>

              {/* Banking Information */}
              <div className="space-y-6 text-xs">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                  <div className="w-2 h-6 bg-purple-500 rounded-full"></div>
                  Banking Information
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FileSignature className="w-4 h-4 text-gray-600" />
                      Account Holder Name
                    </label>
                    <input
                      type="text"
                      name="bankAccountName"
                      value={formData.bankAccountName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 hover:border-gray-300"
                      placeholder="Account holder name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Barcode className="w-4 h-4 text-gray-600" />
                      Account Number
                    </label>
                    <input
                      type="text"
                      name="bankAccountNumber"
                      value={formData.bankAccountNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono hover:border-gray-300"
                      placeholder="Enter account number"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Binary className="w-4 h-4 text-gray-600" />
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    name="ifsc"
                    value={formData.ifsc}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white border rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all duration-200 placeholder:text-gray-400 font-mono ${
                      errors.ifsc
                        ? "border-red-300"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    placeholder="SBIN0001234"
                    maxLength={11}
                    style={{ textTransform: "uppercase" }}
                  />
                  {errors.ifsc && (
                    <p className="mt-1 text-xs text-red-600">{errors.ifsc}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 px-8 py-4 bg-gray-50">
            <div className="flex items-center justify-end gap-3">
              <Button
                type={ButtonType.Cancel}
                onClick={handleClose}
                disabled={loading}
                className="text-sm"
              >
                Cancel
              </Button>
              <Button
                type={ButtonType.Submit}
                onClick={handleSubmit}
                disabled={loading}
                className="text-sm"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {loading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSalesRepModal;
